<template>
    <BaseSidebar
        :is-open="showRedemptionPanel"
        :on-close="close"
        :show-header="true"
        :has-invitation-banner="hasInvitationBanner"
        :custom-classes="dynamicClasses"
    >
        <PanelHeader
            class="sticky bg-white/70 dark:bg-slate-800/70 backdrop-blur top-16 z-20 px-8 pt-6 pb-4"
            :activeRedemption="panelRedemption"
            :is-shared-view="isSharedView"
            :hide-sensitive-data="shouldHideSensitiveData"
            @move-to-trip="showMoveModal = true"
        />

        <!-- Cancellation Status Section -->
        <div
            v-if="
                panelRedemption?.status === 'cancelled' ||
                panelRedemption?.is_modified
            "
            class="px-8 pb-4"
        >
            <div
                v-if="panelRedemption?.status === 'cancelled'"
                class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4"
            >
                <div class="flex items-center gap-3">
                    <Icon
                        icon="x-circle"
                        :width="24"
                        :height="24"
                        class="text-red-500"
                        pathClass="fill-red-500"
                    />
                    <div>
                        <h4 class="text-red-800 font-semibold text-sm">
                            Cancelled
                        </h4>
                        <p class="text-red-600 text-xs mt-1">
                            This redemption was cancelled
                            <span v-if="panelRedemption?.cancelled_at">
                                on
                                {{
                                    dayjs(panelRedemption.cancelled_at).format(
                                        'MMM D, YYYY'
                                    )
                                }}
                            </span>
                        </p>
                    </div>
                </div>
            </div>

            <div
                v-if="panelRedemption?.is_modified"
                class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4"
            >
                <div class="flex items-center gap-3">
                    <Icon
                        icon="edit"
                        :width="24"
                        :height="24"
                        class="text-blue-500"
                        pathClass="fill-blue-500"
                    />
                    <div>
                        <h4 class="text-blue-800 font-semibold text-sm">
                            Modified
                        </h4>
                        <p class="text-blue-600 text-xs mt-1">
                            This redemption has been modified
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="px-8 pb-6 overflow-y-auto">
            <!-- Dynamic components are not working for some reason. I'm leaving this here for this is Excalibur -->
            <!--
                <component
                :is="
                    Filters.slugToSnakeCaseUppercase(
                        activeRedemption.redemption_type?.slug
                    )
                "
                    :activeRedemption="panelRedemption"
                ></component>
                -->

            <Hotel
                v-if="panelRedemption?.redemption_type?.slug === 'hotel'"
                :activeRedemption="panelRedemption"
                :is-shared-view="isSharedView"
                :hide-sensitive-data="shouldHideSensitiveData"
            />

            <Flight
                v-if="panelRedemption?.redemption_type?.slug === 'flight'"
                :activeRedemption="panelRedemption"
                :is-shared-view="isSharedView"
                :hide-sensitive-data="shouldHideSensitiveData"
            />

            <Train
                v-if="panelRedemption?.redemption_type?.slug === 'train'"
                :activeRedemption="panelRedemption"
                :is-shared-view="isSharedView"
                :hide-sensitive-data="shouldHideSensitiveData"
            />

            <RentalCar
                v-if="panelRedemption?.redemption_type?.slug === 'rental-car'"
                :activeRedemption="panelRedemption"
                :is-shared-view="isSharedView"
                :hide-sensitive-data="shouldHideSensitiveData"
            />

            <Other
                v-if="panelRedemption?.redemption_type?.slug === 'other'"
                :activeRedemption="panelRedemption"
                :is-shared-view="isSharedView"
                :hide-sensitive-data="shouldHideSensitiveData"
            />

            <section class="mb-8" v-if="panelRedemption?.notes">
                    <h4 class="text-slate-800 dark:text-slate-100 font-medium mb-4">Notes</h4>
                    <p class="text-slate-800 dark:text-slate-100 text-sm">
                    {{ panelRedemption?.notes }}
                </p>
            </section>

            <PricePayment
                :activeRedemption="panelRedemption"
                :is-shared-view="isSharedView"
                :hide-sensitive-data="shouldHideSensitiveData"
            />

                <div class="py-4 border-t border-slate-200 dark:border-slate-700">
                <RedemptionFiles
                    :files="panelRedemption.attachments"
                    :redemption="panelRedemption"
                    @files-updated="handleFilesUpdated"
                />
            </div>

            <div class="py-4 border-t border-slate-200 dark:border-slate-700" v-if="!isSharedView">
                <RedemptionChangesHistory :redemption="panelRedemption" />
            </div>
        </div>

        <TripSelectorModal
            :isOpen="showMoveModal"
            :redemption="panelRedemption"
            mode="move"
            @close="showMoveModal = false"
            @moved="handleMoved"
            @new-trip="handleNewTripFromMove"
        />

        <CreateTripForRedemptionModal
            :isOpen="showCreateTripModal"
            :redemption="createTripRedemption"
            @close="showCreateTripModal = false"
            @created="handleTripCreatedWithRedemption"
        />
    </BaseSidebar>
</template>
<script setup>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useTripsStore } from '@/store/trips';
import { usePage } from '@inertiajs/vue3';
import { notify } from 'notiwind';
import dayjs from 'dayjs';
import Icon from '@/components/ui/icon/icon.vue';
import BaseSidebar from '@/components/ui/base-sidebar.vue';
import PanelHeader from '@/components/trips/redemptions/panel-partials/panel-header.vue';
import Hotel from '@/components/trips/redemptions/panel-partials/hotel.vue';
import Flight from '@/components/trips/redemptions/panel-partials/flight.vue';
import Train from '@/components/trips/redemptions/panel-partials/train.vue';
import RentalCar from '@/components/trips/redemptions/panel-partials/rental-car.vue';
import Other from '@/components/trips/redemptions/panel-partials/other.vue';
import PricePayment from '@/components/trips/redemptions/panel-partials/price-payment.vue';
import RedemptionFiles from './RedemptionFiles.vue';
import RedemptionChangesHistory from './redemption-changes-history.vue';
import TripSelectorModal from '@/components/trips/modals/trip-selector-modal.vue';
import CreateTripForRedemptionModal from '@/components/trips/modals/create-trip-for-redemption-modal.vue';

const tripsStore = useTripsStore();
const { showRedemptionPanel, panelRedemption, activeTrip } =
    storeToRefs(tripsStore);

const page = usePage();

const showMoveModal = ref(false);
const showCreateTripModal = ref(false);
const createTripRedemption = ref(null);

const props = defineProps({
    isSharedView: {
        type: Boolean,
        default: false,
    },
});

const shouldHideSensitiveData = computed(() => {
    return props.isSharedView && activeTrip.value?.hide_sensitive_share;
});

const hasInvitationBanner = computed(() => {
    const userInvitations = page.props.user_invitations;
    if (!userInvitations || !Array.isArray(userInvitations)) {
        return false;
    }
    const pendingInvites = userInvitations.filter(
        (invite) => invite.status === 'N'
    );
    return pendingInvites.length > 0;
});

const dynamicClasses = computed(() => {
    return [
        '!max-w-[540px]',
        {
            'translate-x-0': showRedemptionPanel.value,
            'translate-x-[540px]': !showRedemptionPanel.value,
            '!top-16': props.isSharedView,
        },
    ];
});

const close = () => {
    tripsStore.setShowRedemptionPanel(false);
    tripsStore.setPanelRedemption(null);
};

const handleFilesUpdated = (files) => {
    if (panelRedemption.value) {
        panelRedemption.value.attachments = files;
        panelRedemption.value.files = files;
    }
};

const handleNewTripFromMove = (redemption) => {
    showMoveModal.value = false;
    createTripRedemption.value = redemption;
    showCreateTripModal.value = true;
};

const handleTripCreatedWithRedemption = ({ trip, redemption }) => {
    showCreateTripModal.value = false;
    close();

    if (activeTrip.value?.uuid) {
        tripsStore.getRedemptions(activeTrip.value.uuid);
    }
};

const handleMoved = (trip) => {
    showMoveModal.value = false;
    close();

    if (activeTrip.value?.uuid) {
        tripsStore.getRedemptions(activeTrip.value.uuid);
    }
};
</script>
